import React, { useEffect, useState } from 'react';
import { BookOpen, Download, Loader2, Printer, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';
import PhotoBookSellDialog from './PhotoBookSellDialog';
import PhotoBookDialog from './PhotoBookDialog';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

// Buy / download buttons for an album's photo book (members see it on the album page; the public page reuses it).
export function PhotoBookBuyButtons({ book, token, compact = false }) {
  const { shopCheckout, photoBookDownloadUrl } = useAuth();
  const [busy, setBusy] = useState('');
  const buy = async (fmt) => {
    const productId = fmt === 'pdf' ? book.product_pdf_id : book.product_print_id;
    setBusy(fmt);
    try {
      const res = await shopCheckout({ items: [{ product_id: productId, qty: 1 }], fulfillment: fmt === 'print' ? 'delivery' : 'pickup', address: null });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(formatApiError(e, 'Could not start checkout')); setBusy(''); }
  };
  const btn = 'inline-flex items-center gap-1.5 rounded font-title text-sm disabled:opacity-50 ' + (compact ? 'px-3 py-1.5' : 'px-4 py-2');
  return (
    <div className="flex flex-wrap gap-2" data-testid="photo-book-buy-buttons">
      {book.can_download ? (
        <a href={photoBookDownloadUrl(book.id, token)} className={btn} style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="photo-book-download"><Download size={15} /> Download PDF {book.reason === 'free' ? '(free for you)' : book.reason === 'purchased' ? '(purchased)' : ''}</a>
      ) : book.sell_pdf && book.product_pdf_id && (
        <button onClick={() => buy('pdf')} disabled={!!busy} className={btn} style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="photo-book-buy-pdf">{busy === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Buy PDF · {money(book.price_pdf)}</button>
      )}
      {book.sell_print && book.product_print_id && (
        <button onClick={() => buy('print')} disabled={!!busy} className={`${btn} border`} style={{ borderColor: 'var(--navy)', color: 'var(--navy)', background: 'white' }} data-testid="photo-book-buy-print">{busy === 'print' ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />} Order printed copy · {money(book.price_print)}{book.postage_print ? ` + ${money(book.postage_print)} postage` : ''}</button>
      )}
    </div>
  );
}

// Album-page panel: listing card for members; admin gets "Sell photo book" + preview build.
export default function PhotoBookPanel({ album, photoCount }) {
  const { bookForAlbum, isAdmin } = useAuth();
  const [book, setBook] = useState(undefined);
  const [sell, setSell] = useState(false);
  const load = () => bookForAlbum(album).then(setBook).catch(() => setBook(null));
  useEffect(() => { load(); }, [album]); // eslint-disable-line react-hooks/exhaustive-deps

  if (book === undefined) return null;
  if (!book && !isAdmin) return null;
  return (
    <div className="mb-4 rounded-lg border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: 'rgba(0,0,0,0.12)', background: 'white' }} data-testid="photo-book-panel">
      <div className="flex items-center gap-3 flex-1 min-w-[220px]">
        <BookOpen size={28} style={{ color: 'var(--maroon)' }} />
        <div>
          <div className="font-title" style={{ color: 'var(--navy)' }} data-testid="photo-book-panel-title">{book ? `Photo book — ${book.title || album}` : 'Photo book'}</div>
          <div className="text-xs" style={{ color: 'var(--olive)' }}>{book ? `${book.pages} A4 pages · ${book.photo_count} photos · captions & index` : 'Turn this album into a printable A4 photo book members can buy or download.'}</div>
        </div>
      </div>
      {book && <PhotoBookBuyButtons book={book} compact />}
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSell(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-title text-sm border" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)', background: 'white' }} data-testid="album-sell-book"><Tag size={14} /> {book ? 'Pricing & sales' : 'Sell photo book'}</button>
          <PhotoBookDialog album={album} count={photoCount} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-title text-sm border" />
        </div>
      )}
      {isAdmin && <PhotoBookSellDialog album={album} open={sell} onOpenChange={setSell} onSaved={() => load()} />}
    </div>
  );
}
